import { API_URL } from "@/constant";
import {
  addCustomerConnectCaneraBankSchema,
  type CustomerConnectCaneraBank,
} from "@/schema/CustomerLogin";
import { useAppDispatch, useAppSelector } from "@/store/hook";
import {
  getAllLanguagesAsync,
  selectAllLanguages,
  selectLanguagesLoading,
} from "@/store/slices/language";
import { setLoader } from "@/store/slices/loader";
import { zodResolver } from "@hookform/resolvers/zod";
import axios from "axios";
import Cookies from "js-cookie";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Input } from "../ui/input";
import { toast } from "react-toastify";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

type IntlTelInputOptions = {
  preferredCountries?: string[];
  utilsScript?: string;
  separateDialCode?: boolean;
  initialCountry?: string;
  nationalMode?: boolean;
  geoIpLookup?: (callback: (countryCode: string) => void) => void;
  allowDropdown?: boolean;
};

declare global {
  interface Window {
    intlTelInput: (input: HTMLElement, options: IntlTelInputOptions) => any;
    intlTelInputGlobals: {
      getCountryData: () => Country[];
    };
  }
}

interface Country {
  name: string;
  iso2: string;
  dialCode: string;
  priority: number;
  areaCodes: string[] | null;
}

// Hook to dynamically load external scripts
const useScript = (url: string) => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!url) return;

    const existingScript = document.querySelector(`script[src="${url}"]`);
    if (existingScript) {
      setLoaded(true);
      return;
    }

    const script = document.createElement("script");
    script.src = url;
    script.async = true;
    script.onload = () => setLoaded(true);

    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, [url]);

  return loaded;
};
function generateCustomSessionID(length = 24) {
  const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
  let id = "";
  for (let i = 0; i < length; i++) {
    id += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return id;
}
export default function ConnectFormCanera() {
  const isLangLoading = useAppSelector(selectLanguagesLoading);
  const response = useAppSelector(selectAllLanguages);
  const Language = response || [];
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector((state) => state.loader.isActive);
  const navigate = useNavigate();

  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpTimer, setOtpTimer] = useState(0);
  const otpTimerRef = useRef<NodeJS.Timeout | null>(null);

  const phoneNumberInputRef = useRef<HTMLInputElement>(null);
  const itiInstance = useRef<any>(null);

  const coreScriptLoaded = useScript(
    "https://cdn.jsdelivr.net/npm/intl-tel-input@18.2.1/build/js/intlTelInput.min.js"
  );
  const utilsScriptLoaded = useScript(
    "https://cdn.jsdelivr.net/npm/intl-tel-input@18.2.1/build/js/utils.js"
  );

  useEffect(() => {
    const existingSessionID = Cookies.get("ServerSessionID");

    if (!existingSessionID) {
      const newSessionID = generateCustomSessionID();
      Cookies.set("ServerSessionID", newSessionID, {
        expires: 36500, // ~100 years
        secure: true,
        sameSite: "Strict",
      });
      console.log("New session ID set:", newSessionID);
    } else {
      console.log("Existing session ID:", existingSessionID);
    }
  }, []);

  const form = useForm<CustomerConnectCaneraBank>({
    resolver: zodResolver(addCustomerConnectCaneraBankSchema),
    defaultValues: {
      first_name: "",
      last_name: "",
      mobile: "",
      language: "",
    },
    mode: "onChange",
  });

  // Initialize phone input
  useEffect(() => {
    if (
      coreScriptLoaded &&
      utilsScriptLoaded &&
      phoneNumberInputRef.current &&
      window.intlTelInput
    ) {
      itiInstance.current = window.intlTelInput(phoneNumberInputRef.current, {
        preferredCountries: ["us", "gb", "in"],
        separateDialCode: true,
        allowDropdown: true,
        initialCountry: "auto",
        geoIpLookup: (callback: (countryCode: string) => void) => {
          fetch("https://ipinfo.io/json")
            .then((response) => response.json())
            .then((data) => {
              const countryCode = data?.country || "us";
              callback(countryCode);
            })
            .catch(() => callback("us"));
        },
        nationalMode: false,
      });
    }

    if (itiInstance.current && phoneNumberInputRef.current) {
      const inputEl = phoneNumberInputRef.current;

      const handleChange = () => {
        const number = itiInstance.current.getNumber();
        form.setValue("mobile", number, { shouldValidate: true });
      };

      inputEl.addEventListener("input", handleChange);
      inputEl.addEventListener("blur", handleChange);

      return () => {
        inputEl.removeEventListener("input", handleChange);
        inputEl.removeEventListener("blur", handleChange);
      };
    }

    return () => {
      if (itiInstance.current) itiInstance.current.destroy();
    };
  }, [coreScriptLoaded, utilsScriptLoaded, form]);

  // OTP Timer
  const startOtpTimer = (duration: number) => {
    setOtpTimer(duration);
    if (otpTimerRef.current) clearInterval(otpTimerRef.current);

    otpTimerRef.current = setInterval(() => {
      setOtpTimer((prev) => {
        if (prev <= 1) {
          clearInterval(otpTimerRef.current!);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };
  const CustomPopup = ({ message, onOk, onCancel }: any) => (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-gray-800 leading-relaxed">{message}</p>

      <div className="flex justify-end gap-2 mt-2">
        <button
          onClick={onCancel}
          className="px-3 py-1 border rounded bg-gray-200 hover:bg-gray-300 text-sm"
        >
          Cancel
        </button>

        <button
          onClick={onOk}
          className="px-3 py-1 border rounded bg-blue-600 text-white hover:bg-blue-700 text-sm"
        >
          OK
        </button>
      </div>
    </div>
  );

  // ✅ Send OTP
  const handleSendOtp = async () => {
    const phoneNumber = itiInstance.current?.getNumber();
    if (!phoneNumber) {
      toast.error("Please enter a valid mobile number");
      return;
    }

    try {
      dispatch(setLoader(true));
      // mock API call - replace with actual OTP API
      // const res = await axios.post(API_URL.SEND_OTP, { mobile: phoneNumber });
      // sessionStorage.setItem("OTPSentNRI", "123"); // mock OTP
      // setOtpSent(true);
      // startOtpTimer(120);
      // toast.info(
      //   "Session Id has been sent to your registered mobile number. The code is valid for 2 minutes.",
      //   { position: "top-center" }
      // );
      const res = await axios.post(API_URL.SEND_OTP, {
        mobno: phoneNumber.replace("+", ""),
      });
      const result = res.data;
      const otpFromResponse = result.d.data;
      if (result.d.ResponseCode === "00") {
        sessionStorage.setItem("OTPSentNRI", otpFromResponse);
        setOtpSent(true);
        startOtpTimer(120);
        toast.info(
          "Session Id has been sent to your registered mobile number. The code is valid for 2 minutes.",
          { position: "top-center" }
        );
      } else if (result.d.ResponseCode === "4927") {
        const toastId = toast.info(
          <CustomPopup
            message={
              "Mobile number not registered with us. Please visit our website, nearest branch, or call 18001030 for more."
            }
            onOk={() => {
              form.reset();
              toast.dismiss(toastId);
            }}
            onCancel={() => toast.dismiss(toastId)}
          />,
          {
            autoClose: false,
            closeOnClick: false,
            position: "top-center",
          }
        );
      }

      // 🔥 OTHER ERROR → Custom Toast
      else {
        const toastId = toast.info(
          <CustomPopup
            message={
              "This service is available for NRI customers only. Please reach out on toll-free number 18001030."
            }
            onOk={() => {
              form.reset();
              toast.dismiss(toastId);
            }}
            onCancel={() => toast.dismiss(toastId)}
          />,
          {
            autoClose: false,
            closeOnClick: false,
            position: "top-center",
          }
        );
      }
    } catch (error) {
      toast.error("Failed to send OTP");
      console.error(error);
    } finally {
      dispatch(setLoader(false));
    }
  };

  // ✅ Verify OTP and Connect
  const handleVerifyOtpAndConnect = async () => {
    const sentOtp = sessionStorage.getItem("OTPSentNRI");
    if (otp.trim() !== sentOtp) {
      toast.error("Invalid OTP. Please try again.");
      return;
    }

    await onSubmit(); // Proceed to call API
  };

  // ✅ Existing Connection API
  const onSubmit = async () => {
    const UserName = Cookies.get("ServerSessionID") || "Guest";
    const values = form.getValues();
    const phoneNumber = itiInstance.current.getNumber();
    values.mobile = phoneNumber;

    const userdata = {
      FirstName: values.first_name,
      LastName: values.last_name,
      PolicyNo: "",
      MobileNo: values.mobile,
      EmailID: "",
      Lob: "1001",
      CampaignID: "1001",
      CustomerSession: UserName,
      CANumber: "",
      SelectedLanguage: values.language || "1",
      Division: "GLOBAL",
      LocID: "1",
      LocName: "GLOBAL",
      BrowserName: navigator.appName,
      BrowserFullVersion: navigator.appVersion,
      BrowserMajorVersion: parseInt(navigator.appVersion, 10),
      AppName: navigator.appName,
      UserAgent: navigator.userAgent,
      HostName: "",
      IPAddress: "",
      BranchName: "",
      BranchCode: "",
      BranchZone: "",
      ClientRequestId: null,
    };

    try {
      dispatch(setLoader(true));
      const response = await axios.post(API_URL.POST_RoutingInteraction, {
        userdata: JSON.stringify(userdata),
      });

      if (response.status !== 200) throw new Error(`HTTP ${response.status}`);

      const result = response.data;
      const requestID = result.d.RequestId;
      const RoomId = result.d.RoomID;

      localStorage.setItem("requestID", requestID);
      localStorage.setItem("RoomId", RoomId);
      localStorage.setItem("videoCallAllowed", "true");
      // localStorage.setItem("customerConnect", JSON.stringify(values));
      localStorage.setItem(
        "customerConnect",
        JSON.stringify({ values, userdata })
      );

      navigate("/videoCall");
    } catch (error) {
      toast.error("Server Error");
      console.error(error);
    } finally {
      dispatch(setLoader(false));
    }
  };

  useEffect(() => {
    dispatch(getAllLanguagesAsync());
  }, []);

  return (
    <div className="relative min-h-fit flex items-center justify-center p-6">
      <link
        rel="stylesheet"
        href="https://cdn.jsdelivr.net/npm/intl-tel-input@18.2.1/build/css/intlTelInput.css"
      />

      <div className="bg-white p-8 rounded-2xl shadow-2xl w-full max-w-md z-10">
        <div className="flex-col">
          <h1 className="md:text-2xl text-xl font-bold text-center text-gray-700 ">
            Fill the form to connect
          </h1>
          <h1 className="md:text-2xl text-xl font-bold text-center text-gray-700 mb-2">
            with our Executive
          </h1>
        </div>

        <Form {...form}>
          <form className="space-y-3" onSubmit={(e) => e.preventDefault()}>
            <FormField
              control={form.control}
              name="first_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-bold">First Name</FormLabel>
                  <FormControl>
                    <Input
                      className="ring-ring  h-10 border border-input border-gray-400 bg-background px-3 py-2 text-sm ring-offset-background file:bottom-0 placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 placeholder:font-semibold"
                      {...field}
                      type="text"
                      placeholder="Enter your First Name"
                      // maxLength={4}
                    />
                  </FormControl>
                  <FormMessage className="text-destructive" />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="last_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-bold">Last Name</FormLabel>
                  <FormControl>
                    <Input
                      className="ring-ring h-10 border border-input border-gray-400 bg-background px-3 py-2 text-sm ring-offset-background file:bottom-0 placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 placeholder:font-semibold"
                      {...field}
                      type="text"
                      placeholder="Enter your Last Name"
                      // maxLength={4}
                    />
                  </FormControl>
                  <FormMessage className="text-destructive" />
                </FormItem>
              )}
            />

            {/* <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-bold">Email ID</FormLabel>
                  <FormControl>
                    <Input
                      className="ring-ring h-10 border border-input border-gray-400 bg-background px-3 py-2 text-sm ring-offset-background file:bottom-0 placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 placeholder:font-semibold"
                      {...field}
                      type="email"
                      placeholder="Enter your email"
                      // disabled={otpSent}
                    />
                  </FormControl>
                  <FormMessage className="text-destructive" />
                </FormItem>
              )}
            /> */}
            {/* Phone Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Mobile Number
              </label>
              <input
                type="tel"
                ref={phoneNumberInputRef}
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-400  rounded-md focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
            <input type="hidden" {...form.register("mobile")} />
            {/* Language */}
            <FormField
              control={form.control}
              name="language"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-bold">Language</FormLabel>
                  <FormControl>
                    <Select
                      defaultValue={field.value}
                      onValueChange={(value) => field.onChange(value)}
                    >
                      <SelectTrigger className="font-semibold border-gray-400">
                        <SelectValue placeholder="Select a language" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-gray-400 ">
                        {isLangLoading ? (
                          <div className="flex justify-center items-center py-2">
                            <svg
                              className="animate-spin h-5 w-5 text-black"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                          </div>
                        ) : (
                          Language.map((language: any) => (
                            <SelectItem
                              className="border-gray-400"
                              key={language.SkillID}
                              value={language.SkillID}
                            >
                              {language.SkillName}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage className="text-destructive" />
                </FormItem>
              )}
            />

            {otpSent && (
              <div>
                <FormLabel className="font-bold mb-2">Enter OTP</FormLabel>
                <Input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  placeholder="Enter OTP"
                  className="ring-ring h-10 border border-input border-gray-400 bg-background px-3 py-2 text-sm ring-offset-background file:bottom-0 placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 placeholder:font-semibold"
                />
                {otpTimer > 0 ? (
                  <p className="text-sm text-gray-500 mt-1">
                    Expires in: {otpTimer}s
                  </p>
                ) : (
                  <Button
                    onClick={handleSendOtp}
                    className="text-blue-600 bg-blue-200  cursor-pointer text-sm mt-1"
                  >
                    Resend OTP
                  </Button>
                )}
              </div>
            )}

            <Button
              type="button"
              onClick={!otpSent ? handleSendOtp : handleVerifyOtpAndConnect}
              disabled={isLoading}
              className="w-full bg-[#13a8e1] text-black font-medium rounded-md py-2 hover:bg-[#e6b800]"
            >
              {isLoading ? (
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-black"
                  viewBox="0 0 24 24"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    className="opacity-25"
                  />
                  <path
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.37 0 0 5.37 0 12h4z"
                    className="opacity-75"
                  />
                </svg>
              ) : null}
              {!otpSent ? "Send OTP" : "Verify & Connect"}
            </Button>
          </form>
        </Form>
      </div>
    </div>
  );
}
